import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import {
  ToggleButtonComponent,
  ToggleButtonSize,
} from '@fc-shared/ui/toggle-button/toggle-button.component';

@Component({
  selector: 'fc-toggle-button-group',
  template: `
    <div
      [class.full-width]="fullWidth()"
      [class.size-sm]="size() === 'sm'"
      [class.disabled]="isDisabled()"
      class="toggle-buttons"
    >
      <ng-content></ng-content>
      <!-- Groups paired with a free-form control can legitimately have no match;
           a zero-width pill would still paint its two 1px borders as a sliver. -->
      @if (activeButton()) {
        <div
          [style.width]="activeBackgroundWidth()"
          [style.left]="activeBackgroundLeft()"
          [class.initialized]="isInitialized()"
          [class.color-success]="activeColor() === 'success'"
          [class.color-primary]="activeColor() === 'primary'"
          class="active-background"
        ></div>
      }
    </div>
  `,
  styles: `
    @use 'var' as *;

    :host {
      display: block;
    }

    .toggle-buttons {
      position: relative;
      display: flex;
      border-radius: 8px;
      background: $secondary-50;
      border: 1px solid $secondary-100;

      &.full-width {
        width: 100%;
        flex: 1;
      }

      &.size-sm {
        padding: 2px;

        .active-background {
          top: 2px;
          bottom: 2px;
        }
      }
    }

    .active-background {
      position: absolute;
      border-radius: 8px;
      background: $white;
      border: 1px solid $secondary-200;
      z-index: 0;
      bottom: -1px;
      top: -1px;

      &.initialized {
        transition:
          left 0.3s,
          background 0.3s,
          border-color 0.3s;
      }

      &.color-success {
        background: $success-600;
        border-color: $success-600;
      }

      &.color-primary {
        background: $primary-600;
        border-color: $primary-600;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ToggleButtonGroupComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  activeIndex = signal<number>(0);
  toggleButtons = contentChildren<ToggleButtonComponent>(ToggleButtonComponent);
  isDisabled = signal<boolean>(false);
  fullWidth = input<boolean>(false);
  size = input<ToggleButtonSize>('md');
  value = model<any>();
  activeButton = computed(() =>
    this.toggleButtons().find((button) => button.selected()),
  );
  activeColor = computed(() => this.activeButton()?.activeColor() ?? 'default');
  activeBackgroundLeft = computed(() => {
    this.width();
    const activeButton = this.activeButton();
    if (!activeButton) return '0';

    return `${activeButton.elRef.nativeElement.offsetLeft}px`;
  });
  ngControl = inject(NgControl, { optional: true, self: true });
  changed = output<any>();
  elRef = inject(ElementRef);
  activeBackgroundWidth = computed(() => {
    this.width();
    const activeButton = this.activeButton();
    if (!activeButton) return '0';

    return `${activeButton.elRef.nativeElement.offsetWidth}px`;
  });
  formControl: FormControl | null = null;

  width = signal(0);
  resizeObserver: ResizeObserver | null = null;
  isInitialized = signal(false);

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    if (this.ngControl && this.ngControl.control instanceof FormControl) {
      this.formControl = this.ngControl.control;
    }
    effect(() => {
      this.toggleButtons().map((button) => {
        button.fullWidth = this.fullWidth();
        button.size.set(this.size());
        button.selected.set(button.value() === this.value());
      });
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.width.set(entry.contentRect.width);
      }
    });
    this.resizeObserver.observe(this.elRef.nativeElement);
  }

  onTouched: any = () => {};
  onChange: any = () => {};

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.isInitialized.set(true));

    this.toggleButtons().map((button, index) => {
      button.onSelect.subscribe(() => {
        this.activeIndex.set(index);
        this.toggleButtons().map((buttonItem) =>
          buttonItem.selected.set(false),
        );
        button.selected.set(true);
        this.onChange(button.value() as any);
        this.changed.emit(button.value() as any);
      });
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  writeValue(value: any): void {
    if (!value) return;
    this.value.set(value);
    this.activeIndex.set(
      this.toggleButtons().findIndex((button) => button.value() === value),
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    if (isDisabled) {
      this.formControl?.disable();
    } else {
      this.formControl?.enable();
    }
  }
}
